/* Eén dag als grafiek op de pagina van een energieoplossing (ADR 0024): de lijnen van zon, batterij,
 * net en verbruik over 24 uur, met de grens van de aansluiting. In de database is het de JSON-kolom
 * `day_profile` op website.solutions: één object
 *   { "title", "caption", "source", "step_minutes", "grid_limit_kw",
 *     "series": { "solar_kw": […], "battery_kw": […], "grid_kw": […], "consumption_kw": […] } }
 * met per reeks 1440 / step_minutes waarden in kW, vanaf 00:00 uur (lib/dagverloop.ts en
 * db/voorstel/2026-10-09-ems-dag.sql in de site-repo).
 *
 * De site toont de grafiek alleen als alles klopt: alles of niets. Wat de site afkeurt, toont ze niet,
 * en de bezoeker merkt daar niets van. Daarom keurt de CRM met dezelfde regels (`oordeel` hieronder is
 * leesDagverloop van de site, regel voor regel) en slaat ze alleen een dag op die de site toont, of een
 * lege kolom.
 *
 * Niemand typt honderden getallen: de reeksen komen uit een tabel die je plakt (`leesTabel`). De kop,
 * het bijschrift, de bronregel en de grens zijn gewone vakken.
 *
 * Dezelfde bescherming als bij "In het kort" (./kerngegevens):
 *   - Het object in het concept is de enige waarheid. Alleen de grens mag daar tijdens het typen een
 *     tekst zijn ("12,5"); geschreven wordt een getal.
 *   - Wat er geschreven wordt, is het object uit de database met alleen de gewijzigde onderdelen
 *     vervangen. Sleutels die de bewerker niet kent, blijven staan.
 *   - Is er niets anders, dan wordt de kolom niet geschreven.
 *   - Herkent de bewerker de vorm in de database niet, dan blijft de kaart alleen-lezen.
 *   - Leegmaken schrijft geen waarde (null): zo noemt het sql-voorstel "geen grafiek".
 *
 * De kolom komt uit een script dat de eigenaar van de database zelf draait. Heeft een rij de sleutel
 * `day_profile` niet, dan wordt hij nooit geschreven (zie ./website). */

export const KOLOM = 'day_profile';
/** Het script in de site-repo (greenvision-site) dat de kolom toevoegt. */
export const SCRIPT = 'db/voorstel/2026-10-09-ems-dag.sql';
export const LABEL = 'Eén dag als grafiek';

/** De stappen die de site kent, in minuten. */
export const STAPPEN = [5, 10, 15, 30, 60];
export const MINUTEN_PER_DAG = 1440;
/** Boven deze waarde is het geen aansluiting van een bedrijf meer, maar een tikfout (W in plaats van kW). */
export const HOOGSTE_KW = 100_000;

export type Reeks = 'solar_kw' | 'battery_kw' | 'grid_kw' | 'consumption_kw';
export type Tekstveld = 'title' | 'caption' | 'source';

/** De vier reeksen, in de volgorde van de kolommen in een geplakte tabel zonder koppen. */
export const REEKSEN: ReadonlyArray<{ sleutel: Reeks; naam: string; kop: RegExp }> = [
  { sleutel: 'solar_kw', naam: 'Zon', kop: /zon|solar|pv|opwek/i },
  { sleutel: 'battery_kw', naam: 'Batterij', kop: /batterij|accu|battery|bess/i },
  { sleutel: 'grid_kw', naam: 'Net', kop: /(^|[^a-z])(net|grid)([^a-z]|$)|afname/i },
  { sleutel: 'consumption_kw', naam: 'Verbruik', kop: /verbruik|consumption|load/i },
];
const naamVan = (sleutel: Reeks): string => REEKSEN.find((r) => r.sleutel === sleutel)!.naam;
/* De volgorde waarin de site de reeksen nakijkt (REEKSEN in lib/dagverloop.ts). Die bepaalt welke fout
 * als eerste wordt genoemd. */
const SITE_VOLGORDE: Reeks[] = ['consumption_kw', 'grid_kw', 'solar_kw', 'battery_kw'];
const TEKSTVELDEN: Tekstveld[] = ['title', 'caption', 'source'];

type Voorwerp = Record<string, unknown>;
const isVoorwerp = (w: unknown): w is Voorwerp => typeof w === 'object' && w !== null && !Array.isArray(w);
const isTekstOfLeeg = (w: unknown): boolean => w == null || typeof w === 'string';
const tekst = (w: unknown): string => (typeof w === 'string' ? w : '');
/* Alleen een echt getal telt, zoals bij de site: een getal als tekst ("12.5"), null of NaN niet. */
const isGetal = (w: unknown): w is number => typeof w === 'number' && Number.isFinite(w);
const soortNaam = (w: unknown): string => (typeof w === 'string' ? 'een losse tekst' : Array.isArray(w) ? 'een rij' : typeof w === 'number' ? 'een getal' : 'een losse waarde');

/** Heeft deze rij de kolom? Een rij uit een database zonder het script heeft de sleutel niet. */
export const heeftDag = (rij: Record<string, unknown> | null | undefined): boolean => !!rij && KOLOM in rij;

/* ── Getallen en tijden in tekst ───────────────────────────────────────────── */

/** "18:15". Middernacht aan het eind van de dag is 24:00. */
export const klok = (minuut: number): string => `${String(Math.floor(minuut / 60)).padStart(2, '0')}:${String(minuut % 60).padStart(2, '0')}`;

/** Een getal zoals het in Nederland staat: een komma voor de decimalen, een punt voor de duizendtallen.
 *  Hooguit `cijfers` achter de komma, zonder nullen aan het eind. Zonder Intl: overal dezelfde uitkomst. */
export function toonGetal(v: number, cijfers = 1): string {
  const [heel, rest = ''] = Math.abs(v).toFixed(cijfers).split('.');
  const staart = rest.replace(/0+$/, '');
  const nul = Number(heel) === 0 && staart === '';
  return `${v < 0 && !nul ? '-' : ''}${heel.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}${staart ? `,${staart}` : ''}`;
}
const kw = (v: number): string => `${toonGetal(v)} kW`;

/* ── Wat de site doet ──────────────────────────────────────────────────────── */

/** Een dag die door de controle van de site is. */
export interface Dag { kop: string; bijschrift?: string; bron: string; stap: number; grens?: number; reeksen: Record<Reeks, number[]> }

/** Het oordeel over een waarde, met de regels van de site. `regel` is letterlijk de reden die de site
 *  in haar logboek zet; `melding` is dezelfde reden in gewone taal, voor in het formulier. `minuut` is
 *  het moment waarop het misgaat, als de regel over één moment gaat. */
export type Oordeel =
  | { soort: 'leeg' }
  | { soort: 'goed'; dag: Dag }
  | { soort: 'afgekeurd'; regel: string; melding: string; minuut?: number };

const af = (regel: string, melding: string, minuut?: number): Oordeel => ({ soort: 'afgekeurd', regel, melding, ...(minuut === undefined ? {} : { minuut }) });
/* Alleen echte tekst telt, zonder spaties aan de randen: zo leest de site een kop of een bronregel. */
const echteTekst = (w: unknown): string | undefined => (typeof w === 'string' && w.trim() ? w.trim() : undefined);

/** De marge waarbinnen het verbruik gelijk moet zijn aan zon + batterij + net: 5% van de grootste
 *  waarde van de dag, en minstens een halve kW (balansMarge in lib/dagverloop.ts). */
export const balansMarge = (schaal: number): number => Math.max(0.5, 0.05 * schaal);

/** Keurt een waarde zoals de site dat doet (leesDagverloop in lib/dagverloop.ts), in dezelfde volgorde:
 *  de eerste regel die niet klopt, is de reden. */
export function oordeel(waarde: unknown): Oordeel {
  if (waarde === null || waarde === undefined) return { soort: 'leeg' };
  if (!isVoorwerp(waarde)) return af('de waarde is geen object', 'Er staat geen dag maar iets anders.');
  if (Object.keys(waarde).length === 0) return { soort: 'leeg' };

  const kop = echteTekst(waarde.title);
  if (!kop) return af('title ontbreekt', 'De kop boven de grafiek ontbreekt.');
  const bron = echteTekst(waarde.source);
  if (!bron) return af('source ontbreekt', 'De bronregel onder de grafiek ontbreekt.');
  const bijschrift = echteTekst(waarde.caption);

  const stap = waarde.step_minutes;
  if (!isGetal(stap) || !STAPPEN.includes(stap)) {
    return af(`step_minutes moet ${STAPPEN.join(', ')} zijn`, waarde.series == null ? 'De reeksen ontbreken: plak de tabel met de dag.' : 'De stap tussen twee waarden moet 5, 10, 15, 30 of 60 minuten zijn.');
  }
  const aantal = MINUTEN_PER_DAG / stap;

  let grens: number | undefined;
  if (waarde.grid_limit_kw !== undefined && waarde.grid_limit_kw !== null) {
    if (!isGetal(waarde.grid_limit_kw) || waarde.grid_limit_kw <= 0 || waarde.grid_limit_kw > HOOGSTE_KW) {
      return af('grid_limit_kw is geen getal boven nul', `De grens van de aansluiting moet een getal boven nul zijn, en hooguit ${toonGetal(HOOGSTE_KW)} kW. Laat het vak leeg als er geen grenslijn hoort.`);
    }
    grens = waarde.grid_limit_kw;
  }

  if (!isVoorwerp(waarde.series)) return af('series ontbreekt', 'De reeksen ontbreken: plak de tabel met de dag.');
  const reeksen = {} as Record<Reeks, number[]>;
  for (const sleutel of SITE_VOLGORDE) {
    const waarden = waarde.series[sleutel];
    const naam = naamVan(sleutel);
    if (!Array.isArray(waarden)) return af(`de reeks ${sleutel} ontbreekt`, `De reeks ${naam} ontbreekt: plak de tabel met alle vier de reeksen.`);
    if (waarden.length !== aantal) {
      return af(`de reeks ${sleutel} heeft ${waarden.length} waarden, verwacht ${aantal}`, `De reeks ${naam} heeft ${waarden.length} waarden. Bij een stap van ${stap} minuten horen er ${aantal}: een hele dag.`);
    }
    if (!waarden.every(isGetal)) return af(`de reeks ${sleutel} bevat iets anders dan getallen`, `In de reeks ${naam} staat iets anders dan een getal.`);
    if (waarden.some((v) => Math.abs(v) > HOOGSTE_KW)) {
      return af(`de reeks ${sleutel} heeft een waarde boven ${HOOGSTE_KW} kW`, `In de reeks ${naam} staat een waarde boven ${toonGetal(HOOGSTE_KW)} kW. Staan de waarden in W in plaats van kW?`);
    }
    reeksen[sleutel] = waarden;
  }

  const onderNul = (sleutel: Reeks): number => reeksen[sleutel].findIndex((v) => v < 0);
  const zon = onderNul('solar_kw');
  if (zon >= 0) return af('solar_kw heeft een waarde onder nul', `De zon staat om ${klok(zon * stap)} onder nul (${kw(reeksen.solar_kw[zon])}). Zon is nooit onder nul.`, zon * stap);
  const verbruik = onderNul('consumption_kw');
  if (verbruik >= 0) {
    return af('consumption_kw heeft een waarde onder nul', `Het verbruik staat om ${klok(verbruik * stap)} onder nul (${kw(reeksen.consumption_kw[verbruik])}). Verbruik is nooit onder nul.`, verbruik * stap);
  }
  if (Math.max(...reeksen.consumption_kw) <= 0) return af('er is de hele dag geen verbruik', 'Er is de hele dag geen verbruik: het verbruik is overal nul.');

  const schaal = Math.max(...SITE_VOLGORDE.map((sleutel) => Math.max(...reeksen[sleutel].map(Math.abs))));
  const marge = balansMarge(schaal);
  for (let i = 0; i < aantal; i++) {
    const som = reeksen.solar_kw[i] + reeksen.battery_kw[i] + reeksen.grid_kw[i];
    if (Math.abs(reeksen.consumption_kw[i] - som) > marge) {
      return af(
        `om ${klok(i * stap)} is het verbruik niet gelijk aan zon + batterij + net`,
        `Om ${klok(i * stap)} is het verbruik (${kw(reeksen.consumption_kw[i])}) niet gelijk aan zon + batterij + net (${kw(som)}). Het verschil mag hooguit ${kw(marge)} zijn. `
        + 'Staat er een teken omgekeerd? De batterij is boven nul als ze levert, het net is boven nul bij afname.',
        i * stap,
      );
    }
  }
  return { soort: 'goed', dag: { kop, ...(bijschrift ? { bijschrift } : {}), bron, stap, ...(grens === undefined ? {} : { grens }), reeksen } };
}

/* ── Lezen ─────────────────────────────────────────────────────────────────── */

/** Is er niets ingevuld? Voor de site is dat geen waarde of een leeg object. */
export const isLeeg = (waarde: unknown): boolean => waarde == null || (isVoorwerp(waarde) && Object.keys(waarde).length === 0);

/** Leest een getal dat iemand typt: "80", "12,5" of "12.5". Geen getal: null. */
export function leesGetal(invoer: string): number | null {
  const t = invoer.trim().replace(/\s*kw$/i, '');
  if (!/^-?\d+(?:[.,]\d+)?$/.test(t)) return null;
  return Number(t.replace(',', '.'));
}

/** `reden` staat er alleen als de vorm onbekend is (ok is dan false). */
export interface Lezing {
  ok: boolean;
  reden?: string;
  kop: string;
  bijschrift: string;
  bron: string;
  /** Wat er in het vak van de grens staat: het getal uit de database met een komma, of wat iemand typt. */
  grens: string;
  stap: number | null;
  /** De reeksen die er staan. Een reeks die ontbreekt, is een lege rij. */
  reeksen: Record<Reeks, number[]>;
}

const GEEN_REEKSEN = (): Record<Reeks, number[]> => ({ solar_kw: [], battery_kw: [], grid_kw: [], consumption_kw: [] });
const LEEG = (): Lezing => ({ ok: true, kop: '', bijschrift: '', bron: '', grens: '', stap: null, reeksen: GEEN_REEKSEN() });
const onbekend = (reden: string): Lezing => ({ ...LEEG(), ok: false, reden });

/** Herkent de bewerker deze vorm? Leeg (null of {}) telt als geen dag. `typend`: in het concept mag de
 *  grens een tekst zijn, zolang iemand hem typt; in de database is een grens een getal. */
export function leesDag(waarde: unknown, typend = false): Lezing {
  if (waarde == null) return LEEG();
  if (!isVoorwerp(waarde)) return onbekend(`Er staat geen dag maar ${soortNaam(waarde)}.`);
  for (const veld of TEKSTVELDEN) if (!isTekstOfLeeg(waarde[veld])) return onbekend(`Bij "${veld}" staat iets anders dan tekst.`);
  const stap = waarde.step_minutes;
  if (stap != null && !isGetal(stap)) return onbekend('Bij "step_minutes" staat iets anders dan een getal.');
  const grens = waarde.grid_limit_kw;
  if (grens != null && !isGetal(grens) && !(typend && typeof grens === 'string')) return onbekend('Bij "grid_limit_kw" staat iets anders dan een getal.');
  const reeksen = GEEN_REEKSEN();
  if (waarde.series != null) {
    if (!isVoorwerp(waarde.series)) return onbekend('Bij "series" staan geen vier reeksen.');
    for (const { sleutel } of REEKSEN) {
      const rij = waarde.series[sleutel];
      if (rij == null) continue;
      if (!Array.isArray(rij) || !rij.every(isGetal)) return onbekend(`De reeks "${sleutel}" is geen rij getallen.`);
      reeksen[sleutel] = rij;
    }
  }
  return {
    ok: true, kop: tekst(waarde.title), bijschrift: tekst(waarde.caption), bron: tekst(waarde.source),
    grens: isGetal(grens) ? String(grens).replace('.', ',') : tekst(grens), stap: isGetal(stap) ? stap : null, reeksen,
  };
}

/* ── Eén onderdeel wijzigen ────────────────────────────────────────────────── */

/** De kop, het bijschrift of de bronregel wijzigen. De rest blijft letterlijk staan; leeg haalt de sleutel weg. */
export function zetTekst(waarde: unknown, veld: Tekstveld, invoer: string): Voorwerp {
  const uit: Voorwerp = isVoorwerp(waarde) ? { ...waarde } : {};
  if (invoer === '') delete uit[veld];
  else uit[veld] = invoer;
  return uit;
}

/** De grens wijzigen. In het concept staat wat iemand typt; geschreven wordt een getal (teSchrijven). */
export function zetGrens(waarde: unknown, invoer: string): Voorwerp {
  const uit: Voorwerp = isVoorwerp(waarde) ? { ...waarde } : {};
  if (invoer === '') delete uit.grid_limit_kw;
  else uit.grid_limit_kw = invoer;
  return uit;
}

/* ── Een geplakte tabel ────────────────────────────────────────────────────── */

export interface Tabel {
  stap: number;
  reeksen: Record<Reeks, number[]>;
  /** Stond er een kolom met de tijd in? Zonder tijd volgt de stap uit het aantal regels. */
  metTijd: boolean;
  /** Iets om na te kijken, zonder dat het de tabel tegenhoudt. */
  letOp?: string;
}
export type Gelezen = { ok: true; tabel: Tabel } | { ok: false; fout: string };
/* Een poging met één scheidingsteken. `kolommenHerkend`: de fout zit in de waarden, niet in de indeling. */
type Poging = Gelezen & { kolommenHerkend?: boolean };

const mis = (fout: string, kolommenHerkend = false): Poging => ({ ok: false, fout, kolommenHerkend });
const VOLGORDE_ZIN = 'de tijd en dan zon, batterij, net en verbruik';
const TIJD_KOP = /tijd|time|datum|date|moment|uur|interval|periode/i;

/** Het tijdstip in een cel, in minuten na middernacht: "00:15", "0:15:00" of "12-09-2026 00:15". Staat
 *  er meer dan één tijd in de cel ("00:15 - 00:30"), dan telt de eerste. */
function leesTijd(cel: string): number | null {
  const m = /(?:^|[^\d:])(\d{1,2}):(\d{2})(?::\d{2})?(?![\d:])/.exec(cel);
  if (!m) return null;
  return Number(m[1]) < 24 && Number(m[2]) < 60 ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Een getal uit een cel. `komma`: de komma is het decimaalteken en een punt scheidt duizendtallen. */
function leesCel(cel: string, komma: boolean): number | null {
  let t = cel.trim().replace(/\s*kw$/i, '').replace(/^[−–]/, '-').replace(/\s/g, '');
  if (komma) {
    if (t.includes('.') && !/^-?\d{1,3}(?:\.\d{3})+(?:,\d+)?$/.test(t)) return null;
    t = t.replace(/\./g, '').replace(',', '.');
  }
  if (!/^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i.test(t)) return null;
  const getal = Number(t);
  return Number.isFinite(getal) ? getal : null;
}

function splits(regel: string, scheiding: string | RegExp): string[] {
  return regel.split(scheiding).map((cel) => cel.trim().replace(/^"(.*)"$/, '$1').trim());
}

/** Eén poging met één scheidingsteken. */
function leesMet(regels: string[], scheiding: string | RegExp, kommaMag: boolean): Poging {
  const rijen = regels.map((regel) => splits(regel, scheiding));
  // Een regel met koppen: nergens een getal, en ergens een letter.
  const eerste = rijen[0];
  const heeftKoppen = eerste.some((cel) => /[a-z]/i.test(cel)) && eerste.every((cel) => leesCel(cel, true) === null && leesCel(cel, false) === null);
  const data = heeftKoppen ? rijen.slice(1) : rijen;
  const regelNr = (i: number): number => i + (heeftKoppen ? 2 : 1);
  if (data.length === 0) return mis('Er staan alleen koppen in de tabel, geen waarden.');

  // Welke kolom is wat.
  let tijdKolom = -1;
  const kolom = {} as Record<Reeks, number>;
  let letOp: string | undefined;
  if (heeftKoppen) {
    for (const { sleutel, naam, kop } of REEKSEN) {
      const gevonden = eerste.map((cel, i) => (kop.test(cel) ? i : -1)).filter((i) => i >= 0);
      if (gevonden.length !== 1) {
        return mis(`${gevonden.length === 0 ? `De kolom ${naam} is niet gevonden in de koppen` : `Er zijn meer kolommen die ${naam} kunnen zijn`}. Gebruik de koppen Tijd, Zon, Batterij, Net en Verbruik, of plak de kolommen zonder koppen in die volgorde.`);
      }
      kolom[sleutel] = gevonden[0];
    }
    // De kolom met de tijd. Staan de datum en de tijd apart, dan telt de kolom waarin een tijd staat.
    const tijdKolommen = eerste.map((cel, i) => (TIJD_KOP.test(cel) && !Object.values(kolom).includes(i) ? i : -1)).filter((i) => i >= 0);
    tijdKolom = tijdKolommen.find((i) => leesTijd(data[0][i] ?? '') !== null) ?? tijdKolommen[0] ?? -1;
    const koppen = REEKSEN.map(({ sleutel }) => eerste[kolom[sleutel]]).join(' ');
    if (/kwh/i.test(koppen)) letOp = 'In de koppen staat kWh. De grafiek rekent met vermogen in kW: staat er energie per kwartier, reken die dan eerst om (maal vier).';
    else if (/\(\s*w\s*\)|\bwatt\b/i.test(koppen)) letOp = 'In de koppen staat W. De grafiek rekent in kW: deel de waarden eerst door duizend.';
  } else if (eerste.length === 5) {
    tijdKolom = 0;
    REEKSEN.forEach(({ sleutel }, i) => { kolom[sleutel] = i + 1; });
  } else if (eerste.length === 4) {
    REEKSEN.forEach(({ sleutel }, i) => { kolom[sleutel] = i; });
  } else {
    return mis(`Regel 1 heeft ${eerste.length} ${eerste.length === 1 ? 'kolom' : 'kolommen'}. Plak vijf kolommen: ${VOLGORDE_ZIN}. Of alleen die vier, zonder de tijd.`);
  }
  const breedte = eerste.length;

  // Het decimaalteken: staat er ergens een komma tussen twee cijfers, dan is de komma het decimaalteken.
  const getalCellen = data.flatMap((rij) => REEKSEN.map(({ sleutel }) => rij[kolom[sleutel]] ?? ''));
  const komma = kommaMag && getalCellen.some((cel) => /\d,\d/.test(cel));

  const reeksen = GEEN_REEKSEN();
  const tijden: number[] = [];
  for (const [i, rij] of data.entries()) {
    if (rij.length !== breedte) return mis(`Regel ${regelNr(i)} heeft ${rij.length} ${rij.length === 1 ? 'kolom' : 'kolommen'}, de eerste regel ${breedte}.`, true);
    if (tijdKolom >= 0) {
      const minuut = leesTijd(rij[tijdKolom]);
      if (minuut === null) return mis(`Regel ${regelNr(i)}: "${rij[tijdKolom]}" is geen tijd. Schrijf de tijd als 00:15.`, true);
      tijden.push(minuut);
    }
    for (const { sleutel, naam } of REEKSEN) {
      const getal = leesCel(rij[kolom[sleutel]], komma);
      if (getal === null) return mis(`Regel ${regelNr(i)}: bij ${naam} staat ${rij[kolom[sleutel]] === '' ? 'niets' : `"${rij[kolom[sleutel]]}"`}. Daar hoort een getal in kW.`, true);
      reeksen[sleutel].push(getal);
    }
  }

  const n = data.length;
  let stap: number;
  if (tijdKolom >= 0) {
    if (tijden[0] !== 0) {
      return mis(`De tabel begint om ${klok(tijden[0])}. De eerste regel hoort 00:00 te zijn: elke waarde geldt voor het tijdvak dat op die tijd begint. Staan de waarden wel in de goede volgorde vanaf middernacht, plak dan alleen de vier kolommen, zonder de tijd.`, true);
    }
    if (n < 2) return mis('De tabel heeft één regel met waarden. Een hele dag is 96 regels (elk kwartier).', true);
    stap = tijden[1] - tijden[0];
    if (!STAPPEN.includes(stap)) return mis(`Tussen de eerste twee regels zit ${stap} minuten. De stap moet 5, 10, 15, 30 of 60 minuten zijn.`, true);
    for (let i = 1; i < n; i++) {
      if (tijden[i] - tijden[i - 1] !== stap) {
        return mis(`Na ${klok(tijden[i - 1])} volgt ${klok(tijden[i])}: de stap is niet overal ${stap} minuten. Ontbreekt er een regel of staat er een dubbel? Op een dag waarop de klok verspringt, klopt de dag niet: kies dan een andere dag.`, true);
      }
    }
    const aantal = MINUTEN_PER_DAG / stap;
    if (n !== aantal) return mis(`De tabel heeft ${n} regels met waarden, tot en met ${klok(tijden[n - 1])}. Bij een stap van ${stap} minuten horen er ${aantal}: een hele dag, tot en met ${klok(MINUTEN_PER_DAG - stap)}.`, true);
  } else {
    stap = MINUTEN_PER_DAG / n;
    if (!STAPPEN.includes(stap)) return mis(`De tabel heeft ${n} ${n === 1 ? 'regel' : 'regels'} met waarden. Een hele dag is 96 regels (elk kwartier), of 24, 48, 144 of 288.`, true);
  }
  return { ok: true, tabel: { stap, reeksen, metTijd: tijdKolom >= 0, ...(letOp ? { letOp } : {}) } };
}

/** Leest een tabel die uit Excel of een csv is geplakt: de tijd en de vier reeksen in kW, één regel per
 *  tijdvak, met of zonder koppen. Een komma of een punt als decimaalteken mag allebei. */
export function leesTabel(invoer: string): Gelezen {
  const regels = invoer.split(/\r?\n/).filter((regel) => regel.trim() !== '');
  if (regels.length === 0) return { ok: false, fout: 'Er staat niets in het vak.' };
  // Excel plakt met tabs; een csv heeft een puntkomma of een komma. Bij een komma als scheiding is het
  // decimaalteken een punt. De eerste scheiding die een hele tabel oplevert, telt.
  const kandidaten: Array<[string | RegExp, boolean]> = [];
  if (regels[0].includes('\t')) kandidaten.push(['\t', true]);
  if (regels[0].includes(';')) kandidaten.push([';', true]);
  if (regels[0].includes(',')) kandidaten.push([',', false]);
  kandidaten.push([/\s+/, true]);
  const mislukt: Poging[] = [];
  for (const [scheiding, kommaMag] of kandidaten) {
    const { kolommenHerkend, ...uitkomst } = leesMet(regels.map((regel) => regel.trim()), scheiding, kommaMag);
    if (uitkomst.ok) return uitkomst;
    mislukt.push({ ...uitkomst, kolommenHerkend });
  }
  // De melding van de poging die het verst kwam: liever "regel 40 klopt niet" dan "regel 1 heeft 3 kolommen".
  const { fout } = (mislukt.find((poging) => poging.kolommenHerkend) ?? mislukt[0]) as { fout: string };
  return { ok: false, fout };
}

/** De reeksen en de stap uit een geplakte tabel in het concept zetten. De kop, de bronregel, de grens en
 *  wat de bewerker niet kent, blijven staan. */
export function zetTabel(waarde: unknown, tabel: Tabel): Voorwerp {
  const uit: Voorwerp = isVoorwerp(waarde) ? { ...waarde } : {};
  uit.step_minutes = tabel.stap;
  uit.series = { ...(isVoorwerp(uit.series) ? uit.series : {}), ...tabel.reeksen };
  return uit;
}

/* ── Wat ervan begrepen is ─────────────────────────────────────────────────── */

export interface ReeksSamenvatting { sleutel: Reeks; naam: string; aantal: number; laagste: number; laagsteOm: string; hoogste: number; hoogsteOm: string }

export interface Samenvatting {
  /** Het aantal punten per reeks, of null als de reeksen niet even lang zijn. */
  punten: number | null;
  stap: number | null;
  reeksen: ReeksSamenvatting[];
}

/** Wat er van de reeksen begrepen is: per reeks het aantal punten en de laagste en de hoogste waarde,
 *  met het tijdstip erbij. Null als er geen reeksen zijn. */
export function samenvatting(waarde: unknown): Samenvatting | null {
  const lezing = leesDag(waarde, true);
  if (!lezing.ok) return null;
  const gevuld = REEKSEN.filter(({ sleutel }) => lezing.reeksen[sleutel].length > 0);
  if (gevuld.length === 0) return null;
  const stap = lezing.stap !== null && STAPPEN.includes(lezing.stap) ? lezing.stap : null;
  const om = (i: number): string => (stap === null ? `punt ${i + 1}` : klok(i * stap));
  const reeksen = gevuld.map(({ sleutel, naam }): ReeksSamenvatting => {
    const rij = lezing.reeksen[sleutel];
    let laag = 0;
    let hoog = 0;
    rij.forEach((v, i) => { if (v < rij[laag]) laag = i; if (v > rij[hoog]) hoog = i; });
    return { sleutel, naam, aantal: rij.length, laagste: rij[laag], laagsteOm: om(laag), hoogste: rij[hoog], hoogsteOm: om(hoog) };
  });
  const lengtes = new Set(REEKSEN.map(({ sleutel }) => lezing.reeksen[sleutel].length));
  return { punten: lengtes.size === 1 ? [...lengtes][0] : null, stap, reeksen };
}

/* ── Vergelijken, nakijken, schrijven ──────────────────────────────────────── */

/* De database (jsonb) bewaart de volgorde van sleutels niet: vergelijk dus op inhoud. */
function canoniek(w: unknown): string {
  if (Array.isArray(w)) return `[${w.map(canoniek).join(',')}]`;
  if (isVoorwerp(w)) return `{${Object.keys(w).filter((k) => w[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${canoniek(w[k])}`).join(',')}}`;
  return JSON.stringify(w) ?? 'null';
}

const zelfdeRij = (a: number[], b: number[]): boolean => a.length === b.length && a.every((v, i) => v === b[i]);

/** De grens in het concept als getal, `null` als het vak leeg is, of 'fout' als er geen getal staat. */
function grensVan(waarde: Voorwerp): number | null | 'fout' {
  const g = waarde.grid_limit_kw;
  if (g == null || (typeof g === 'string' && g.trim() === '')) return null;
  if (isGetal(g)) return g;
  return typeof g === 'string' ? leesGetal(g) ?? 'fout' : 'fout';
}

/** Het object zoals het geschreven zou worden: de database als basis, met alleen wat anders is
 *  vervangen. `gewijzigd` zegt of er iets anders is. Nog niet gekeurd. Beide vormen zijn bekend. */
function samengesteld(opgeslagen: unknown, concept: unknown): { waarde: Voorwerp | null; gewijzigd: boolean } {
  if (isLeeg(concept)) return { waarde: null, gewijzigd: !isLeeg(opgeslagen) };
  const oud: Voorwerp = isVoorwerp(opgeslagen) ? opgeslagen : {};
  const nieuw = concept as Voorwerp;
  const uit: Voorwerp = { ...oud };
  let gewijzigd = false;
  for (const veld of TEKSTVELDEN) {
    const t = tekst(nieuw[veld]).trim();
    if (t === tekst(oud[veld]).trim()) continue;
    gewijzigd = true;
    if (t === '') delete uit[veld];
    else uit[veld] = t;
  }
  const grens = grensVan(nieuw);
  if (grens !== (isGetal(oud.grid_limit_kw) ? oud.grid_limit_kw : null)) {
    gewijzigd = true;
    if (grens === null) delete uit.grid_limit_kw;
    else uit.grid_limit_kw = grens === 'fout' ? nieuw.grid_limit_kw : grens;
  }
  if (isGetal(nieuw.step_minutes) && nieuw.step_minutes !== oud.step_minutes) {
    gewijzigd = true;
    uit.step_minutes = nieuw.step_minutes;
  }
  const oudeReeksen = leesDag(oud).reeksen;
  const nieuweReeksen = leesDag(nieuw, true).reeksen;
  const anders = REEKSEN.filter(({ sleutel }) => !zelfdeRij(oudeReeksen[sleutel], nieuweReeksen[sleutel]));
  if (anders.length > 0) {
    gewijzigd = true;
    const series: Voorwerp = isVoorwerp(oud.series) ? { ...oud.series } : {};
    for (const { sleutel } of anders) series[sleutel] = nieuweReeksen[sleutel];
    uit.series = series;
  }
  return { waarde: uit, gewijzigd };
}

export type Staat = 'leeg' | 'compleet' | 'niet_af';

/** Wat de site met het concept zou doen, voor de regel onder de vakken: de staat, en bij een dag die
 *  niet compleet is de reden in gewone taal, met het moment waarop het misgaat als de regel daarover gaat. */
export function staatVan(opgeslagen: unknown, concept: unknown): { staat: Staat; reden?: string; minuut?: number } {
  if (!leesDag(opgeslagen).ok || !leesDag(concept, true).ok) return { staat: 'niet_af' };
  const { waarde } = samengesteld(opgeslagen, concept);
  if (waarde !== null && grensVan(concept as Voorwerp) === 'fout') return { staat: 'niet_af', reden: GEEN_GETAL };
  const o = oordeel(waarde);
  if (o.soort === 'afgekeurd') return { staat: 'niet_af', reden: o.melding, ...(o.minuut === undefined ? {} : { minuut: o.minuut }) };
  return { staat: o.soort === 'leeg' ? 'leeg' : 'compleet' };
}

export const STAAT_ZIN: Record<Staat, string> = {
  leeg: 'Leeg: op de pagina van deze oplossing staat geen grafiek.',
  compleet: 'Compleet: met deze dag toont de site de grafiek.',
  niet_af: 'Nog niet compleet: de site toont de grafiek alleen als alles klopt. Zo is het niet op te slaan.',
};

const GEEN_GETAL = 'De grens van de aansluiting is geen getal. Schrijf hem zo: 80 of 12,5.';

/** Wat er mis is met de dag, of null. Alleen een dag die is aangeraakt, wordt nagekeken: oude inhoud
 *  houdt het opslaan van een ander veld niet tegen. Nagekeken wordt de hele dag zoals hij geschreven zou
 *  worden, met de regels van de site. */
export function controleerDag(opgeslagen: unknown, concept: unknown): string | null {
  if (!leesDag(opgeslagen).ok) {
    return canoniek(opgeslagen) === canoniek(concept) ? null : `${LABEL}: deze dag heeft een vorm die de bewerker niet kent. Hij is hier niet te wijzigen.`;
  }
  const nieuw = leesDag(concept, true);
  if (!nieuw.ok) return `${LABEL}: ${nieuw.reden}`;
  const { waarde, gewijzigd } = samengesteld(opgeslagen, concept);
  if (!gewijzigd || waarde === null) return null;
  if (grensVan(concept as Voorwerp) === 'fout') return `${LABEL}: ${GEEN_GETAL}`;
  const o = oordeel(waarde);
  return o.soort === 'afgekeurd' ? `${LABEL}: ${o.melding}` : null;
}

/** Wat er geschreven moet worden, of null als er niets te schrijven is: niets gewijzigd, een onbekende
 *  vorm in de database of in het concept, of een dag die de site niet zou tonen. `waarde: null` is een
 *  lege kolom: leegmaken. */
export function teSchrijven(opgeslagen: unknown, concept: unknown): { waarde: Voorwerp | null } | null {
  if (!leesDag(opgeslagen).ok || !leesDag(concept, true).ok || controleerDag(opgeslagen, concept)) return null;
  const { waarde, gewijzigd } = samengesteld(opgeslagen, concept);
  if (!gewijzigd) return null;
  // Een object zonder sleutels is voor de site hetzelfde als geen waarde: schrijf dan geen waarde.
  return { waarde: waarde !== null && Object.keys(waarde).length === 0 ? null : waarde };
}
